import { useMemo } from 'react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Search, Users, Check, X, Loader2, Sparkles } from 'lucide-react';
import { DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { fuzzyTokenMatch, maxEditsForToken, suggestCorrection } from '../../../lib/fuzzy';

/**
 * Step 1 of the Make-Intros dialog: search + pick exactly two contacts.
 *
 * Stateless — all picked / search state owned by `MakeIntrosButton`.
 * Filtering + "Did you mean…?" suggestion are derived purely from props.
 */
export default function IntrosPickerStep({
  contacts,
  search,
  setSearch,
  picked,
  pickedContacts,
  onTogglePick,
  creating,
  onContinue,
}) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = contacts.filter((c) => !c.deleted_at && c.source !== 'self_card');
    if (q) {
      const qTokens = q.split(/\s+/).filter(Boolean);
      list = list.filter((c) => {
        const text = [
          c.first_name, c.last_name, c.middle_name, c.nickname,
          ...(c.phones || []).map((p) => p.number || ''),
          ...(c.emails || []).map((e) => e.email || ''),
        ].filter(Boolean).join(' ').toLowerCase();
        const sTokens = text.split(/[\s@./_\-]+/).filter(Boolean);
        return qTokens.every((qt) => {
          const budget = maxEditsForToken(qt);
          return sTokens.some((st) => fuzzyTokenMatch(qt, st, budget));
        });
      });
    }
    return list.slice(0, 50);
  }, [contacts, search]);

  const suggestion = useMemo(() => {
    if (!search.trim()) return null;
    return suggestCorrection(search, filtered);
  }, [search, filtered]);

  return (
    <div className="flex flex-col max-h-[80vh]">
      <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
        <DialogTitle className="font-heading flex items-center gap-2">
          <Users size={18} className="text-amber-600" />
          Make intros
        </DialogTitle>
        <DialogDescription className="text-xs">
          Pick two contacts you&apos;d like to introduce. We&apos;ll generate a personalized link for each.
        </DialogDescription>
      </DialogHeader>

      {/* Search + picked count */}
      <div className="px-5 pt-3 pb-2 space-y-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search by name, phone, or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            /* `text-base` (16px) is critical: iOS Safari auto-zooms any
               input with font-size < 16px on focus, which made the dialog
               overflow viewport edges. */
            className="pl-9 h-11 text-base"
            data-testid="make-intros-search"
          />
        </div>
        {pickedContacts.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="make-intros-picked-chips">
            {pickedContacts.map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-xs"
              >
                {c.first_name} {c.last_name}
                <button onClick={() => onTogglePick(c.id)} className="ml-0.5">
                  <X size={10} />
                </button>
              </span>
            ))}
            <span className="text-[11px] text-muted-foreground self-center">
              {pickedContacts.length}/2 selected
            </span>
          </div>
        )}
      </div>

      {/* Contact list */}
      <div className="flex-1 overflow-y-auto px-2 pb-2" data-testid="make-intros-contact-list">
        {suggestion && suggestion.correctedQuery.toLowerCase() !== search.trim().toLowerCase() && (
          <button
            type="button"
            onClick={() => setSearch(suggestion.correctedQuery)}
            className="w-full flex items-center gap-2 px-3 py-2 mb-1 text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-500/5 rounded-md transition-colors"
            data-testid="make-intros-did-you-mean"
          >
            <Sparkles size={12} className="text-amber-500 shrink-0" />
            <span>
              Did you mean
              <span className="font-semibold underline decoration-dotted ml-1">
                {suggestion.correctedQuery}
              </span>
              <span className="text-amber-700/70 dark:text-amber-300/70">?</span>
            </span>
          </button>
        )}
        {filtered.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            {search ? 'No matches.' : 'No contacts yet.'}
          </p>
        ) : (
          filtered.map((c) => {
            const isPicked = picked.includes(c.id);
            const disabled = !isPicked && picked.length >= 2;
            return (
              <button
                key={c.id}
                onClick={() => onTogglePick(c.id)}
                disabled={disabled}
                className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isPicked
                    ? 'bg-amber-500/10 border border-amber-500/30'
                    : disabled
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:bg-secondary/60'
                }`}
                data-testid={`make-intros-row-${c.id}`}
              >
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
                  isPicked ? 'bg-amber-500 border-amber-500' : 'border-border'
                }`}>
                  {isPicked && <Check size={12} className="text-white" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">
                    {c.first_name} {c.last_name}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {(c.phones?.[0]?.number) || (c.emails?.[0]?.email) || (c.work?.company) || '—'}
                  </p>
                </div>
              </button>
            );
          })
        )}
      </div>

      <div className="px-5 py-3 border-t border-border bg-card">
        <Button
          onClick={onContinue}
          disabled={picked.length !== 2 || creating}
          className="w-full h-11 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-heading font-semibold"
          data-testid="make-intros-continue-btn"
        >
          {creating ? <Loader2 size={16} className="animate-spin" /> : 'Continue'}
        </Button>
      </div>
    </div>
  );
}
